import { useEffect, useState } from "react";
import api from "../lib/api";

/**
 * Lightweight fetchers + primitives for rendering equipped cosmetics from
 * the Hifireels Store across the app.
 *
 * useCosmetics(userId) → { equipped } (memoized 5 min per userId)
 * <AvatarFrame userId src ...> → img with the user's equipped frame ring
 * <UserName userId>{fallback children}</UserName> → gradient nickname if set
 */

// tiny in-memory cache
const CACHE = new Map(); // userId → { equipped, at }
const TTL = 5 * 60 * 1000;

export function useCosmetics(userId) {
  const [equipped, setEquipped] = useState(() => {
    const c = userId && CACHE.get(userId);
    return c && Date.now() - c.at < TTL ? c.equipped : {};
  });
  useEffect(() => {
    if (!userId) return;
    const c = CACHE.get(userId);
    if (c && Date.now() - c.at < TTL) { setEquipped(c.equipped); return; }
    api.get(`/users/${userId}/cosmetics`)
      .then(r => {
        const eq = r.data?.equipped || {};
        CACHE.set(userId, { equipped: eq, at: Date.now() });
        setEquipped(eq);
      })
      .catch(() => {});
  }, [userId]);
  return equipped;
}

export function AvatarFrame({ userId, src, alt = "", size = 40, className = "", ...rest }) {
  const eq = useCosmetics(userId);
  const frame = eq.frame?.payload || null;
  const s = size;
  const style = frame ? {
    borderColor: frame.color || "#FFD700",
    borderWidth: 3,
    boxShadow: frame.glow ? `0 0 12px ${frame.color || "#FFD700"}` : undefined,
  } : { borderWidth: 0 };
  const animCls = frame?.animated ? " animate-pulse" : "";
  return (
    <span className={`inline-block rounded-full overflow-hidden border-solid ${className}${animCls}`}
      style={{ width: s, height: s, ...style }}
      {...rest}>
      <img src={src} alt={alt} className="w-full h-full object-cover"/>
    </span>
  );
}

export function UserName({ userId, children, className = "" }) {
  const eq = useCosmetics(userId);
  const nick = eq.nickname_color?.payload || null;
  if (!nick?.gradient) return <span className={className}>{children}</span>;
  return <span className={`bg-gradient-to-r ${nick.gradient} bg-clip-text text-transparent font-extrabold ${nick.animated ? "animate-pulse" : ""} ${className}`}>{children}</span>;
}
